import type { EventPayloadMap, EventType, GamePlatformEvent } from './events';

type PayloadFor<T extends EventType> = EventPayloadMap[T];

// Per-event colour so it's easy to eyeball a busy console during a flow
// (create → purchase → reveal → sell). Falls back to a neutral colour for
// events added later that forget to register.
const EVENT_COLORS: Partial<Record<EventType, string>> = {
  session_ready: '#60A5FA',
  session_expired: '#F87171',
  purchase_initiated: '#FBBF24',
  purchase_completed: '#22C55E',
  purchase_failed: '#EF4444',
  reveal_started: '#A855F7',
  item_revealed: '#22E11C',
  item_sold: '#14B8A6',
  reveal_complete: '#8B5CF6',
  balance_updated: '#38BDF8',
  shipping_failed: '#F97316',
  navigate_back_result: '#F472B6',
  navigate_lobby: '#FB923C',
  navigate_deposit: '#FACC15',
  haptic: '#C084FC',
  error: '#DC2626',
};

export function emit<T extends EventType>(
  type: T,
  ...args: PayloadFor<T> extends undefined ? [] : [payload: PayloadFor<T>]
): void {
  const msg: GamePlatformEvent<T> = { source: 'repackz', type };
  if (args.length > 0) msg.payload = args[0];
  window.parent.postMessage(msg, '*');

  // Dev‑only debug logging so the whole purchase/debit/credit lifecycle is
  // visible in the browser console. Prod bundles strip this branch
  // (Vite replaces `import.meta.env.DEV` with `false` in production).
  if (import.meta.env.DEV) {
    const color = EVENT_COLORS[type] ?? '#9CA3AF';
    console.log(
      `%c[gp-event] ${type}`,
      `color: ${color}; font-weight: 600`,
      args[0] ?? '',
    );
  }
}
